(() => {
  'use strict';

  const SELECTORS = {
    page: '[data-member-page]',
    config: '[data-member-config]',
  };

  const numberFormatter = new Intl.NumberFormat('ja-JP');
  const dateFormatter = new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const formatNumber = (value) => numberFormatter.format(Number(value) || 0);
  let disposeMemberPage;

  function parseDate(value) {
    if (!value) return null;
    const date = new Date(String(value).replace(' ', 'T'));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function createElement(tagName, className, textContent) {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (textContent !== undefined) element.textContent = textContent;
    return element;
  }

  function showToast(message) {
    const current = document.querySelector('.raitooe-member-toast');
    if (current) current.remove();

    const toast = createElement('div', 'raitooe-member-toast', message);
    toast.setAttribute('role', 'status');
    (document.querySelector('dialog[open]') || document.body).appendChild(toast);
    window.setTimeout(() => toast.remove(), 3200);
  }

  async function copyText(value) {
    if (navigator.clipboard && window.isSecureContext) {
      let timer;
      try {
        await Promise.race([
          navigator.clipboard.writeText(value),
          new Promise((_, reject) => { timer = window.setTimeout(() => reject(new Error('Clipboard timeout')), 2000); }),
        ]);
        return;
      } catch (error) {
      } finally {
        window.clearTimeout(timer);
      }
    }

    const input = document.createElement('textarea');
    input.value = value;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    const active = document.activeElement;
    (document.querySelector('dialog[open]') || document.body).appendChild(input);
    input.select();
    const copied = document.execCommand('copy');
    input.remove();
    active?.focus({ preventScroll: true });
    if (!copied) throw new Error('Clipboard unavailable');
  }

  function initializeMemberPage(event) {
    const page = document.querySelector(SELECTORS.page);
    if (page?.dataset.memberInitialized === 'true' && event?.type !== 'shopify:section:load') return;
    if (disposeMemberPage) disposeMemberPage();
    if (!page) return;

    const configNode = page.querySelector(SELECTORS.config);
    if (!configNode) return;

    let config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (error) {
      return;
    }

    page.dataset.memberInitialized = 'true';
    document.body.classList.add('raitooe-member-template');
    const lifecycle = new AbortController();
    disposeMemberPage = () => {
      lifecycle.abort();
      delete page.dataset.memberInitialized;
    };
    const listen = (target, type, handler, options = {}) => {
      target.addEventListener(type, handler, { ...options, signal: lifecycle.signal });
    };

    const apiBase = String(config.apiBase || '/apps/user_points').replace(/\/$/, '');
    const state = {
      balance: 0,
      rewards: [],
      rewardFilter: 'all',
      selectedReward: null,
      historyMode: 'all',
      histories: {},
      redeemPending: false,
      redeemUncertain: false,
    };

    async function request(path, options = {}) {
      const controller = new AbortController();
      const cancel = () => controller.abort();
      lifecycle.signal.addEventListener('abort', cancel, { once: true });
      const timeout = window.setTimeout(cancel, 15000);
      try {
        const response = await fetch(`${apiBase}${path}`, {
          credentials: 'same-origin',
          signal: controller.signal,
          ...options,
          headers: { Accept: 'application/json', ...(options.headers || {}) },
        });
        const data = await response.json().catch(() => null);
        if (lifecycle.signal.aborted) throw new Error('Aborted');
        if (!response.ok || !data) throw new Error(data?.msg || `Request failed (${response.status})`);
        return data;
      } finally {
        window.clearTimeout(timeout);
        lifecycle.signal.removeEventListener('abort', cancel);
      }
    }

    function requireLogin() {
      if (config.loggedIn) return false;
      if (!window.RaitooeAccountLogin?.open({ trigger: document.activeElement })) {
        showToast('右上のアカウントからログインしてください。');
      }
      return true;
    }

    function setBalance(value) {
      if (!config.loggedIn) return; // 未登录保留主题的示例余额
      state.balance = Math.max(0, Number(value) || 0);
      document.querySelectorAll('[data-member-balance]').forEach((element) => {
        element.textContent = formatNumber(state.balance);
      });
    }

    listen(document, 'raitooe:points-updated', event => {
      if (Number.isSafeInteger(event.detail?.balance) && event.detail.balance >= 0) setBalance(event.detail.balance);
    });

    function setReadBalance(value, revision) {
      const points = window.RaitooePoints;
      if (points) {
        points.accept(value, 'member-read', revision);
        if (points.get().balance !== null) setBalance(points.get().balance);
      } else setBalance(value);
    }

    function initDirectory() {
      const slot = page.querySelector('[data-member-directory-slot]');
      const directory = page.querySelector('[data-member-directory]');
      if (!slot || !directory) return;

      const links = Array.from(directory.querySelectorAll('a[href^="#member-"]'));
      const sections = links
        .map((link) => document.querySelector(link.getAttribute('href')))
        .filter(Boolean);
      let queued = false;

      const getHeaderBottom = () => {
        const header = document.querySelector('#header-group');
        return header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      };

      const update = () => {
        queued = false;
        if (lifecycle.signal.aborted) return;
        const headerBottom = getHeaderBottom();
        const shouldFix = slot.getBoundingClientRect().top <= headerBottom;
        directory.classList.toggle('is-fixed', shouldFix);
        directory.style.top = shouldFix ? `${headerBottom}px` : '';

        const activeOffset = headerBottom + directory.offsetHeight + 28;
        let activeSection = sections[0];
        sections.forEach((section) => {
          if (!section.hidden && section.getBoundingClientRect().top <= activeOffset) activeSection = section;
        });

        links.forEach((link) => {
          const active = activeSection && link.hash === `#${activeSection.id}`;
          link.classList.toggle('is-active', active);
          if (active) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      };

      const queueUpdate = () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(update);
      };

      listen(window, 'scroll', queueUpdate, { passive: true });
      const scroller = document.querySelector('.page-wrapper');
      if (scroller) listen(scroller, 'scroll', queueUpdate, { passive: true });
      listen(window, 'resize', queueUpdate);
      update();
    }

    function markCurrentRank(levelName) {
      const normalized = String(levelName || '').trim().toLocaleLowerCase();
      document.querySelectorAll('[data-member-rank-name]').forEach((row) => {
        const active = row.dataset.memberRankName.trim().toLocaleLowerCase() === normalized;
        row.classList.toggle('is-current', active);
        if (active) {
          const label = row.querySelector('[data-member-rank-state]');
          if (label) label.textContent = '現在のランク';
        }
      });
    }

    // 等级表按后台配置渲染；后台未配置任何等级 → 整块隐藏（访客与登录用户一致）
    async function loadPublicLevels() {
      const data = await request('/Point/getPublicConfig');
      if (data.code !== 200) throw new Error('Levels unavailable');
      const levels = Array.isArray(data.levels) ? data.levels : [];
      const rankSection = document.querySelector('#member-rank');

      if (!levels.length) {
        // 主题编辑器里不隐藏，方便运营看到并配置该区块
        if (!config.designMode) {
          if (rankSection) rankSection.hidden = true;
          document.querySelectorAll('a[href="#member-rank"]').forEach((el) => { el.hidden = true; });
          // 后台没有等级配置 → 首屏的等级/倍率/进度一并隐藏（与登录用户路径一致），不再展示示例值
          document.querySelectorAll('.member-account-facts, [data-member-progress]').forEach((el) => { el.hidden = true; });
        }
        return true;
      }

      // 访客：首屏等级卡按后台的「入门等级」显示，替代写死的示例值
      if (!config.loggedIn) {
        const levelEl = document.querySelector('[data-member-level]');
        const multEl = document.querySelector('[data-member-multiplier]');
        const nextEl = document.querySelector('[data-member-next-level]');
        if (levelEl) levelEl.textContent = levels[0].name || '—';
        if (multEl) multEl.textContent = `${Number(levels[0].points_multiplier) || 1}${config.multiplierUnit}`;
        if (nextEl) nextEl.textContent = levels[1] ? levels[1].name : config.maxLevelLabel;
      }

      const table = document.querySelector('.member-rank-table');
      if (table) {
        table.querySelectorAll('.member-rank-row:not(.member-rank-row-head)').forEach((row) => row.remove());
        levels.forEach((lv) => {
          const row = createElement('div', 'member-rank-row');
          row.setAttribute('role', 'row');
          row.setAttribute('data-member-rank-name', lv.name || '');
          const nameCell = createElement('span');
          nameCell.setAttribute('role', 'cell');
          nameCell.appendChild(createElement('b', '', lv.name || ''));
          nameCell.appendChild(createElement('small', '', '')).setAttribute('data-member-rank-state', '');
          row.appendChild(nameCell);
          const growthCell = createElement('span', '', `${formatNumber(Number(lv.min_growth) || 0)}〜`);
          growthCell.setAttribute('role', 'cell');
          row.appendChild(growthCell);
          const multCell = createElement('strong', '', `${Number(lv.points_multiplier) || 1}${config.multiplierUnit}`);
          multCell.setAttribute('role', 'cell');
          row.appendChild(multCell);
          table.appendChild(row);
        });
      }
      return true;
    }

    async function loadLevel() {
      await loadPublicLevels().catch(() => {});
      const data = await request('/MemberLevel/customerLevel');
      if (data.code !== 200) throw new Error(data.msg || 'Level unavailable');
      const enabled = Boolean(data.has_levels && data.current_level);
      document.querySelectorAll('.member-account-facts, [data-member-progress]').forEach(el => { el.hidden = !enabled; });
      const rankSection = document.querySelector('#member-rank');
      if (rankSection) rankSection.hidden = !enabled && !config.designMode;
      document.querySelectorAll('a[href="#member-rank"]').forEach(el => { el.hidden = !enabled && !config.designMode; });
      if (!enabled) return true;

      const current = data.current_level;
      const next = data.next_level;
      const progress = Math.max(0, Math.min(100, Number(data.progress_percent) || 0));

      const level = document.querySelector('[data-member-level]');
      const multiplier = document.querySelector('[data-member-multiplier]');
      const nextLevel = document.querySelector('[data-member-next-level]');
      const remaining = document.querySelector('[data-member-growth-remaining]');
      const growth = document.querySelector('[data-member-growth-value]');
      const track = document.querySelector('[data-member-progress-track]');
      const fill = document.querySelector('[data-member-progress-fill]');

      if (level) level.textContent = current.name || '—';
      if (multiplier) multiplier.textContent = `${Number(current.points_multiplier) || 1}${config.multiplierUnit}`;
      if (nextLevel) nextLevel.textContent = next?.name || config.maxLevelLabel;
      if (remaining) {
        remaining.textContent = next
          ? `${config.remainingPrefix}${formatNumber(Math.max(0, Number(next.min_growth) - Number(data.total_growth)))}`
          : '';
      }
      if (growth) {
        growth.textContent = next
          ? `${formatNumber(data.total_growth)} / ${formatNumber(next.min_growth)} ${config.growthUnit}`
          : `${formatNumber(data.total_growth)} ${config.growthUnit}`;
      }
      if (track) track.setAttribute('aria-valuenow', String(next ? progress : 100));
      if (fill) fill.style.width = `${next ? progress : 100}%`;

      markCurrentRank(current.name);
      return true;
    }

    function renderCheckin(data) {
      const container = document.querySelector('[data-member-checkin]');
      if (!container) return;

      const streak = container.querySelector('[data-member-streak]');
      const hint = container.querySelector('[data-member-checkin-hint]');
      const button = container.querySelector('[data-member-checkin-button]');
      const status = container.querySelector('[data-member-checkin-status]');
      const basePoints = Math.max(0, Number(data.base_points) || 0);
      const multipliers = [1, 1, 1.5, 1.5, 2, 2, 3];
      const nextStreak = data.checked_today ? Math.max(1, Number(data.streak)) : Math.max(0, Number(data.streak)) + 1;
      const currentDay = ((nextStreak - 1) % 7) + 1;
      const todayPoints = Math.round(basePoints * (nextStreak === 21 ? 5 : multipliers[currentDay - 1]));
      const completedDay = data.checked_today ? currentDay : currentDay - 1;

      if (streak && config.loggedIn) streak.textContent = formatNumber(data.streak);
      if (hint) {
        if (!config.loggedIn) {
          hint.textContent = data.enabled
            ? 'ログインすると、今日のチェックインポイントを受け取れます。'
            : '現在、デイリーチェックインは休止中です。';
        } else {
          hint.textContent = data.enabled
            ? (data.checked_today
              ? '本日のチェックインは完了しました。次回も続けてボーナスを受け取りましょう。'
              : `今日のチェックインで +${formatNumber(todayPoints)} pt。7日目は3倍、21日連続で5倍になります。`)
            : '現在、デイリーチェックインは休止中です。';
        }
      }
      if (status) status.textContent = '';

      container.querySelectorAll('[data-member-checkin-day]').forEach((item) => {
        const day = Number(item.dataset.memberCheckinDay);
        const points = day === currentDay && nextStreak === 21 ? Math.round(basePoints * 5) : Math.round(basePoints * multipliers[day - 1]);
        const value = item.querySelector('b');
        if (value) value.textContent = `+${formatNumber(points)}`;
        item.classList.toggle('is-done', day <= completedDay);
        item.classList.toggle('is-current', day === currentDay);
      });

      if (button) {
        if (!config.loggedIn) {
          button.disabled = !data.enabled;
          button.textContent = data.enabled ? 'ログインしてチェックイン' : '現在休止中です';
        } else {
          button.disabled = !data.enabled || Boolean(data.checked_today);
          button.textContent = data.checked_today
            ? '本日はチェックイン済み'
            : (data.enabled ? `今日の +${formatNumber(todayPoints)} pt を受け取る` : '現在休止中です');
        }
      }
    }

    async function loadCheckin() {
      const revision = window.RaitooePoints?.revision();
      const data = await request('/Checkin/status');
      if (data.code !== 200) throw new Error(data.msg || 'Check-in unavailable');
      setReadBalance(data.rawBalance ?? data.balance, revision);
      renderCheckin(data);
      return true;
    }

    function bindCheckin() {
      const button = document.querySelector('[data-member-checkin-button]');
      if (!button) return;

      listen(button, 'click', async () => {
        if (button.disabled) return;
        if (config.designMode) {
          showToast(config.designModeNotice);
          return;
        }
        if (requireLogin()) return;

        const status = document.querySelector('[data-member-checkin-status]');
        button.disabled = true;
        if (status) status.textContent = '処理中です…';

        try {
          const data = await request('/Checkin/claim', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          });
          if (data.code !== 200) throw new Error(data.msg || 'Check-in failed');
          window.RaitooePoints?.accept(data.balance, 'checkin');
          setBalance(data.balance);
          showToast(data.earned > 0 ? `チェックインで ${formatNumber(data.earned)} pt を獲得しました。` : '本日のチェックインは完了済みです。');
          button.textContent = '本日はチェックイン済み';
          resetPointsHistory();
          const refresh = await Promise.allSettled([loadCheckin(), loadRewards()]);
          if (lifecycle.signal.aborted) return;
          if (refresh.some(result => result.status === 'rejected') && status) status.textContent = 'チェックインは完了しました。最新情報はページを再読み込みしてご確認ください。';
        } catch (error) {
          if (lifecycle.signal.aborted) return;
          button.disabled = false;
          if (status) status.textContent = 'チェックインできませんでした。時間をおいてお試しください。';
        }
      });
    }

    async function loadEarnRules() {
      const data = await request('/Point/getEarnRules');
      if (data.code !== 200) throw new Error(data.msg || 'Earn rules unavailable');

      const rules = new Map((data.rules || []).map((rule) => [String(rule.id), rule]));
      document.querySelectorAll('[data-member-earn-rule]').forEach((row) => {
        const id = String(row.dataset.memberEarnRule || '');
        const value = row.querySelector('[data-member-earn-value]');
        row.style.display = '';
        if (!id) return; // 未绑定插件规则：文案由主题人工维护
        const rule = rules.get(id);
        if (!rule) { row.style.display = 'none'; return; } // 绑定了规则但插件已停用/积分为 0：隐藏该行
        // 仅在插件返回正积分时覆盖；消费返点（按比例、无固定积分）等保留人工文案
        if (value && Number(rule.points) > 0) value.textContent = `+${formatNumber(rule.points)} pt`;
      });
      return true;
    }

    function collectRewardMetadata() {
      const metadata = new Map();
      document.querySelectorAll('[data-member-reward-preview]').forEach((card) => {
        metadata.set(String(card.dataset.ruleId || ''), {
          label: card.dataset.rewardLabel || 'メンバー限定',
          description: card.dataset.rewardDescription || '',
          image: card.dataset.rewardImage || '',
          imageAlt: card.dataset.rewardImageAlt || '',
        });
      });
      return metadata;
    }

    const rewardMetadata = collectRewardMetadata();

    function getRewardAvailability(rule) {
      if (!config.loggedIn) return { label: 'ログインして交換', available: true, near: false };
      if (Number(rule.remaining_stock) === 0) return { label: '在庫切れ', available: false, near: false };
      if (Number(rule.max_per_customer) > 0 && Number(rule.customer_redeemed) >= Number(rule.max_per_customer)) {
        return { label: '交換上限', available: false, near: false };
      }
      if (state.balance < Number(rule.points_required)) {
        return {
          label: `あと ${formatNumber(Number(rule.points_required) - state.balance)} pt`,
          available: false,
          near: true,
        };
      }
      return { label: rule.can_redeem ? '交換可能' : '現在交換できません', available: Boolean(rule.can_redeem), near: false };
    }

    function rewardVisualValue(rule) {
      const amount = Number(rule.discount_amount) || 0;
      if (!amount) return 'POINT';
      if (String(rule.discount_type).toLowerCase().includes('percent')) return `${formatNumber(amount)}%`;
      return `¥${formatNumber(amount)}`;
    }

    function buildRewardCard(rule) {
      const meta = rewardMetadata.get(String(rule.id)) || {};
      const availability = getRewardAvailability(rule);
      const card = createElement('article', 'member-reward-card');
      // 图片与说明优先取插件配置（redeem_rule.gift_image / remark），主题 blocks 仅作后备
      const imageUrl = rule.image || meta.image || '';
      const description = rule.description || meta.description || '';
      Object.assign(card.dataset, {
        memberRewardPreview: '', ruleId: String(rule.id), rewardLabel: meta.label || '',
        rewardDescription: description, rewardImage: imageUrl, rewardImageAlt: meta.imageAlt || '',
      });
      const visual = createElement('div', `member-reward-visual${imageUrl ? '' : ' member-reward-visual-generic'}`);

      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = meta.imageAlt || rule.title || '';
        image.loading = 'lazy';
        visual.appendChild(image);
      } else {
        visual.appendChild(createElement('span', '', rewardVisualValue(rule)));
        visual.appendChild(createElement('small', '', 'POINT REWARD'));
      }

      if (Number(rule.remaining_stock) > 0) {
        visual.appendChild(createElement('em', '', `残り ${formatNumber(rule.remaining_stock)}`));
      }

      const content = createElement('div', 'member-reward-content');
      const metaRow = createElement('div', 'member-reward-meta');
      metaRow.appendChild(createElement('span', '', meta.label || 'メンバー限定'));
      metaRow.appendChild(createElement('span', '', availability.label));
      content.appendChild(metaRow);
      content.appendChild(createElement('h3', '', rule.title || 'ポイント特典'));
      content.appendChild(createElement('p', '', description || 'ポイントを使って交換できる会員限定特典です。'));

      const footer = createElement('div', 'member-reward-footer');
      footer.appendChild(createElement('strong', '', `${formatNumber(rule.points_required)} ${config.pointsUnit}`));
      const button = createElement('button', '', (!config.loggedIn || !availability.available) ? availability.label : '交換する');
      button.type = 'button';
      button.disabled = !availability.available;
      listen(button, 'click', () => openRedeemDialog(rule));
      footer.appendChild(button);
      content.appendChild(footer);
      card.append(visual, content);
      return card;
    }

    function renderRewards() {
      const list = document.querySelector('[data-member-reward-list]');
      const empty = document.querySelector('[data-member-reward-empty]');
      const status = document.querySelector('[data-member-reward-status]');
      if (!list) return;

      const rules = state.rewards.filter((rule) => {
        const availability = getRewardAvailability(rule);
        if (state.rewardFilter === 'available') return availability.available;
        if (state.rewardFilter === 'near') return availability.near;
        return true;
      });

      list.replaceChildren(...rules.map(buildRewardCard));
      list.setAttribute('aria-busy', 'false');
      if (empty) empty.hidden = rules.length > 0;
      if (status) status.textContent = state.rewards.length ? '特典内容と必要ポイントはポイント管理画面の設定を反映しています。' : '';
    }

    async function loadRewards() {
      const revision = window.RaitooePoints?.revision();
      const data = window.RaitooePoints ? await window.RaitooePoints.read() : await request('/Redeem/getRules');
      if (data.code !== 200) throw new Error(data.msg || 'Rewards unavailable');
      setReadBalance(data.rawBalance ?? data.balance, revision);
      state.rewards = data.rules || [];
      renderRewards();
      return true;
    }

    function bindRewardFilters() {
      const buttons = document.querySelectorAll('[data-member-reward-filter]');
      buttons.forEach((button) => {
        if (!config.loggedIn) {
          button.disabled = button.dataset.memberRewardFilter !== 'all';
          return;
        }

        listen(button, 'click', () => {
          state.rewardFilter = button.dataset.memberRewardFilter;
          buttons.forEach((item) => {
            item.classList.toggle('is-active', item === button);
            item.setAttribute('aria-pressed', String(item === button));
          });
          renderRewards();
        });
      });
    }

    function openRedeemDialog(rule) {
      const dialog = document.querySelector('[data-member-redeem-dialog]');
      if (!dialog) return;
      if (requireLogin()) return;
      if (state.redeemPending || state.redeemUncertain) {
        showToast('交換履歴を確認してから、ページを再読み込みしてください。');
        return;
      }

      state.selectedReward = rule;
      state.dialogTrigger = document.activeElement;
      dialog.querySelector('[data-member-dialog-title]').textContent = rule.title || '特典を交換';
      dialog.querySelector('[data-member-dialog-cost]').textContent = `${formatNumber(rule.points_required)} ${config.pointsUnit}`;
      dialog.querySelector('[data-member-dialog-remaining]').textContent = `${formatNumber(Math.max(0, state.balance - Number(rule.points_required)))} ${config.pointsUnit}`;
      dialog.querySelector('[data-member-redeem-confirmation]').hidden = false;
      dialog.querySelector('[data-member-redeem-result]').hidden = true;
      dialog.querySelector('[data-member-coupon-wrap]').hidden = true;
      dialog.querySelector('[data-member-prizes-link]').hidden = true;
      dialog.querySelector('[data-member-dialog-status]').textContent = '';
      dialog.showModal();
    }

    function bindRedeemDialog() {
      const dialog = document.querySelector('[data-member-redeem-dialog]');
      if (!dialog) return;

      dialog.querySelectorAll('[data-member-dialog-close]').forEach((button) => {
        listen(button, 'click', () => { if (!state.redeemPending) dialog.close(); });
      });
      listen(dialog, 'cancel', event => { if (state.redeemPending) event.preventDefault(); });
      listen(dialog, 'close', () => {
        const target = state.dialogTrigger?.isConnected ? state.dialogTrigger : document.querySelector('[data-member-reward-filter="all"]');
        target?.focus({ preventScroll: true });
      });
      listen(dialog, 'click', (event) => {
        const bounds = dialog.getBoundingClientRect();
        if (event.target === dialog && !state.redeemPending && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) dialog.close();
      });

      const confirm = dialog.querySelector('[data-member-redeem-confirm]');
      listen(confirm, 'click', async () => {
        if (!state.selectedReward || state.redeemPending || state.redeemUncertain) return;
        if (config.designMode) {
          showToast(config.designModeNotice);
          return;
        }

        const status = dialog.querySelector('[data-member-dialog-status]');
        confirm.disabled = true;
        state.redeemPending = true;
        let accepted = false;
        let rejected = false;
        status.textContent = '交換処理中です…';

        try {
          const data = await request('/Redeem/redeem', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ rule_id: state.selectedReward.id }),
          });
          if (data.code !== 200) {
            rejected = true;
            throw new Error(data.msg || 'Redeem failed');
          }
          accepted = true;

          dialog.querySelector('[data-member-redeem-confirmation]').hidden = true;
          const result = dialog.querySelector('[data-member-redeem-result]');
          const resultText = dialog.querySelector('[data-member-redeem-result-text]');
          const couponWrap = dialog.querySelector('[data-member-coupon-wrap]');
          const prizesLink = dialog.querySelector('[data-member-prizes-link]');
          result.hidden = false;
          status.textContent = '';

          if (data.coupon_code) {
            dialog.querySelector('[data-member-coupon-code]').textContent = data.coupon_code;
            couponWrap.hidden = false;
            resultText.textContent = '下記のクーポンコードをお買い物時にご利用ください。';
          } else {
            resultText.textContent = data.gift_name
              ? `「${data.gift_name}」を交換しました。マイ賞品から詳細をご確認ください。`
              : '特典を交換しました。マイ賞品から詳細をご確認ください。';
            prizesLink.hidden = false;
          }

          if (window.RaitooePoints) void window.RaitooePoints.invalidate('redeem').catch(() => {});
          else setBalance(Math.max(0, state.balance - Number(state.selectedReward.points_required)));
          resetPointsHistory();
          delete state.histories.redeem;
          const refresh = await Promise.allSettled([loadRewards(), ...(state.historyMode === 'redeem' ? [loadHistory(true)] : [])]);
          if (!lifecycle.signal.aborted && refresh.some(result => result.status === 'rejected' || result.value === false)) status.textContent = '交換は完了しました。最新の残高・履歴はページを再読み込みしてご確認ください。';
        } catch (error) {
          if (lifecycle.signal.aborted) return;
          const message = String(error.message || '').toLowerCase();
          if (!accepted && !rejected) {
            state.redeemUncertain = true;
            status.textContent = '通信が途切れたため交換結果を確認できません。重複交換を避けるため、交換履歴を確認してください。';
          } else if (accepted) status.textContent = '交換は完了しました。最新情報はページを再読み込みしてご確認ください。';
          else if (message.includes('insufficient')) status.textContent = 'ポイントが不足しています。';
          else if (message.includes('limit')) status.textContent = 'この特典の交換上限に達しています。';
          else if (message.includes('stock')) status.textContent = 'この特典は在庫切れです。';
          else status.textContent = '交換できませんでした。時間をおいてもう一度お試しください。';
        } finally {
          state.redeemPending = false;
          confirm.disabled = state.redeemUncertain;
        }
      });

      listen(dialog.querySelector('[data-member-copy-coupon]'), 'click', async () => {
        const code = dialog.querySelector('[data-member-coupon-code]').textContent;
        const copyButton = dialog.querySelector('[data-member-copy-coupon]');
        const status = dialog.querySelector('[data-member-dialog-status]');
        copyButton.disabled = true;
        try {
          await copyText(code);
          status.textContent = 'クーポンコードをコピーしました。';
        } catch (error) {
          status.textContent = 'コピーできませんでした。コードを選択してコピーしてください。';
        } finally {
          copyButton.disabled = false;
        }
      });
    }

    function historyLabel(item) {
      const labels = {
        'orders/paid': 'お買い物ポイント',
        daily_checkin: 'デイリーチェックイン',
        redeem: '特典交換',
        share_order_referral: '友だち紹介',
        share_referral: '友だち紹介',
        first_order_bonus: '初回購入ボーナス',
        'customers/create': '会員登録',
        customer_created: '会員登録',
        admin_sync_signup: '会員登録',
        onboarding_step: 'ガイド完了',
        custom_earn: 'アンケート完了',
        add_to_cart: 'カート追加',
      };
      if (labels[item.event_type]) return labels[item.event_type];
      if (String(item.event_type || '').startsWith('lottery_')) return 'キャンペーン';
      return item.description || item.event_type || 'ポイント更新';
    }

    function historyDate(value) {
      const date = parseDate(value);
      return date ? dateFormatter.format(date) : String(value || '');
    }

    function buildPointsHistoryItem(item) {
      const isEarn = Number(item.type) === 1;
      const article = createElement('article');
      const time = createElement('time', '', historyDate(item.created_at));
      const icon = createElement('span', `member-history-icon ${isEarn ? 'is-earn' : 'is-spend'}`, isEarn ? '+' : '−');
      const copy = createElement('div');
      copy.appendChild(createElement('strong', '', historyLabel(item)));
      copy.appendChild(createElement('small', '', item.description || item.event_type || ''));
      const amount = createElement('b', '', `${isEarn ? '+' : '−'}${formatNumber(Math.abs(Number(item.points_change) || 0))} pt`);
      article.append(time, icon, copy, amount);
      return article;
    }

    function buildRedeemHistoryItem(item) {
      const article = createElement('article');
      const time = createElement('time', '', historyDate(item.created_at));
      const icon = createElement('span', 'member-history-icon is-spend', '✓');
      const copy = createElement('div');
      copy.appendChild(createElement('strong', '', item.rule_title || 'ポイント特典'));
      copy.appendChild(createElement('small', '', `${formatNumber(item.points_spent)} pt を利用`));

      let action;
      if (item.coupon_code) {
        action = createElement('button', 'member-history-copy-code', item.coupon_code);
        action.type = 'button';
        action.setAttribute('aria-label', `${item.coupon_code} コピー`);
        const feedback = createElement('small');
        feedback.setAttribute('role', 'status');
        copy.appendChild(feedback);
        listen(action, 'click', async () => {
          action.disabled = true;
          try {
            await copyText(item.coupon_code);
            feedback.textContent = 'クーポンコードをコピーしました。';
          } catch (error) {
            feedback.textContent = 'コピーできませんでした。コードを選択してコピーしてください。';
          } finally {
            action.disabled = false;
          }
        });
      } else {
        action = createElement('b', '', '交換済み');
      }

      article.append(time, icon, copy, action);
      return article;
    }

    function renderHistory() {
      const list = document.querySelector('[data-member-history-list]');
      const more = document.querySelector('[data-member-history-more]');
      if (!list) return;

      const cache = historyCache();
      const items = cache.items.map(state.historyMode === 'redeem' ? buildRedeemHistoryItem : buildPointsHistoryItem);

      if (!items.length) {
        const empty = createElement('div', 'member-empty-state');
        empty.appendChild(createElement('h3', '', cache.loading ? '読み込み中…' : cache.error ? '履歴を読み込めませんでした' : 'まだ履歴はありません'));
        if (!cache.loading && !cache.error) empty.appendChild(createElement('p', '', 'ポイントの獲得や特典交換を行うと、ここに記録が表示されます。'));
        list.replaceChildren(empty);
      } else {
        list.replaceChildren(...items);
      }
      if (cache.error) {
        const message = createElement('p', '', '読み込みに失敗しました。再試行しても表示済みの記録は失われません。');
        message.setAttribute('role', 'status');
        list.appendChild(message);
      }
      list.setAttribute('aria-busy', String(cache.loading));
      if (more) {
        more.dataset.memberMoreLabel ||= more.textContent || 'さらに表示';
        more.hidden = !cache.hasMore && !cache.error;
        more.disabled = cache.loading;
        more.textContent = cache.loading ? '読み込み中…' : cache.error ? 'もう一度読み込む' : more.dataset.memberMoreLabel;
      }
    }

    function historyCache(mode = state.historyMode) {
      return state.histories[mode] ||= { items: [], page: 0, hasMore: false, loaded: false, loading: false, error: false };
    }

    async function loadHistory(reset = false) {
      const mode = state.historyMode;
      if (reset) delete state.histories[mode];
      const cache = historyCache(mode);
      if (cache.loading) return true;
      cache.loading = true;
      cache.error = false;
      renderHistory();
      try {
        const pageNumber = cache.page + 1;
        const endpoint = mode === 'redeem' ? '/Redeem/getHistory' : '/Point/getPointsHistory';
        const filter = mode === 'earn' ? '&type=1' : mode === 'spend' ? '&type=2' : '';
        const data = await request(`${endpoint}?page=${pageNumber}${filter}`);
        if (data.code !== 200) throw new Error(data.msg || 'History unavailable');
        if (state.histories[mode] !== cache) return true;
        const seen = new Set(cache.items.filter(item => item.id != null).map(item => String(item.id)));
        cache.items.push(...(data.list || []).filter(item => {
          if (item.id == null) return true;
          const id = String(item.id);
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        }));
        cache.page = pageNumber;
        cache.hasMore = Boolean(data.has_more);
        cache.loaded = true;
        return true;
      } catch (error) {
        cache.error = true;
        return false;
      } finally {
        cache.loading = false;
        if (!lifecycle.signal.aborted && mode === state.historyMode && state.histories[mode] === cache) renderHistory();
      }
    }

    function resetPointsHistory() {
      ['all', 'earn', 'spend'].forEach(mode => { delete state.histories[mode]; });
      if (state.historyMode !== 'redeem' && document.querySelector('[data-member-history-list]')) {
        loadHistory(true);
      }
    }

    function bindHistory() {
      const history = document.querySelector('[data-member-history]');
      if (!history) return;

      const tabs = history.querySelectorAll('[data-member-history-tab]');
      tabs.forEach((tab) => {
        listen(tab, 'click', async () => {
          state.historyMode = tab.dataset.memberHistoryTab;
          tabs.forEach((item) => {
            const active = item === tab;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-selected', String(active));
            item.tabIndex = active ? 0 : -1;
          });

          renderHistory();
          if (!historyCache().loaded && !historyCache().loading) await loadHistory();
        });
        tab.tabIndex = tab.dataset.memberHistoryTab === state.historyMode ? 0 : -1;
        listen(tab, 'keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const index = Array.from(tabs).indexOf(tab);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          tabs[next].focus({ preventScroll: true });
          tabs[next].click();
        });
      });

      const more = history.querySelector('[data-member-history-more]');
      if (more) listen(more, 'click', () => loadHistory());
    }

    function bindReferral() {
      document.querySelectorAll('[data-member-referral]').forEach((button) => {
        listen(button, 'click', async () => {
          if (config.designMode) {
            showToast(config.designModeNotice);
            return;
          }
          if (requireLogin()) return;

          button.disabled = true;
          try {
            const data = await request('/Share/generateCode');
            if (data.code !== 200) throw new Error(data.msg || 'Referral unavailable');
            const url = data.share_url || `${window.location.origin}/?ref=${data.share_code}`;
            await copyText(url);
            showToast('紹介リンクをコピーしました。');
          } catch (error) {
            showToast('紹介リンクを取得できませんでした。');
          } finally {
            button.disabled = false;
          }
        });
      });
    }

    initDirectory();
    bindRewardFilters();
    bindRedeemDialog();
    bindCheckin();
    bindHistory();
    bindReferral();

    if (!config.loggedIn) {
      const hint = document.querySelector('[data-member-checkin-hint]');
      if (hint) hint.textContent = 'ログインすると、今日のチェックインポイントを受け取れます。';
      // 未登录也拉公开数据：积分规则、奖励目录、签到倍率、等级表按插件当前配置展示；个人数据保留主题示例
      Promise.allSettled([loadEarnRules(), loadRewards(), loadCheckin(), loadPublicLevels()]);
      return;
    }

    const account = document.querySelector('[data-member-account]');
    const accountStatus = document.querySelector('[data-member-account-status]');
    const initialLoads = [loadLevel(), loadRewards(), loadCheckin(), loadEarnRules(), loadHistory(true)];
    Promise.allSettled(initialLoads).then((results) => {
      if (lifecycle.signal.aborted) return;
      if (account) account.setAttribute('aria-busy', 'false');
      if (results[0].status === 'rejected') {
        document.querySelectorAll('[data-member-progress], .member-account-facts, #member-rank, a[href="#member-rank"]').forEach(el => { el.hidden = true; });
      }
      if (results[1].status === 'rejected') {
        const list = document.querySelector('[data-member-reward-list]');
        if (list) {
          list.replaceChildren(createElement('p', 'member-empty-state', '特典を読み込めませんでした。ページを再読み込みしてください。'));
          list.setAttribute('aria-busy', 'false');
        }
      }
      if (results[2].status === 'rejected') {
        const button = document.querySelector('[data-member-checkin-button]');
        if (button) { button.disabled = true; button.textContent = 'チェックイン情報を取得できませんでした'; }
        const hint = document.querySelector('[data-member-checkin-hint]');
        if (hint) hint.textContent = 'ページを再読み込みして、もう一度お試しください。';
      }
      if (results[3].status === 'rejected') {
        document.querySelectorAll('[data-member-earn-value]').forEach(el => { el.textContent = '—'; });
      }
      const successful = results.filter((result) => result.status === 'fulfilled' && result.value !== false).length;
      if (accountStatus) accountStatus.textContent = successful === results.length ? '' : successful ? '一部の情報を読み込めませんでした。ページを再読み込みしてください。' : config.loadError;
      if (successful !== results.length && accountStatus) {
        const retry = createElement('button', 'member-load-more', config.retryLabel || '再読み込み');
        retry.type = 'button';
        listen(retry, 'click', () => window.location.reload());
        accountStatus.appendChild(retry);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeMemberPage, { once: true });
  } else {
    initializeMemberPage();
  }
  document.addEventListener('shopify:section:load', initializeMemberPage);
})();
